/**
 * Shared date formatting utilities.
 *
 * All user-facing dates should go through these helpers
 * so the format stays consistent across the application.
 */

/** Matches YYYY-MM-DD (with optional trailing content after a "T"). */
const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Format a date string as MM/DD/YYYY.
 * Handles ISO timestamps (2025-08-06T00:00:00+00:00), date-only (2025-08-06),
 * and already-formatted strings. Returns the raw string if parsing fails.
 *
 * Date-only strings (YYYY-MM-DD) are parsed without `new Date()` to avoid
 * the ECMAScript UTC-midnight interpretation that shifts dates in local time.
 */
export function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';

  // For date-only strings, parse directly to avoid timezone shift.
  const dateOnly = DATE_ONLY_RE.exec(dateStr);
  if (dateOnly) {
    const [, y, m, d] = dateOnly;
    return `${m}/${d}/${y}`;
  }

  // Treat midnight ISO timestamps as date-only to avoid timezone shift
  // (e.g., "2024-01-15T00:00:00+00:00" should display as 01/15/2024, not 01/14/2024)
  const midnightMatch = /^(\d{4})-(\d{2})-(\d{2})T00:00:00/.exec(dateStr);
  if (midnightMatch) {
    const [, y, m, d] = midnightMatch;
    return `${m}/${d}/${y}`;
  }

  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;

  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const year = d.getFullYear();
  return `${month}/${day}/${year}`;
}

/**
 * Format a date string as MM/DD/YYYY hh:mm AM/PM.
 * Falls back to formatDate (no time) if the input has no time portion.
 */
export function formatDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';

  // Date-only strings: parse directly to avoid timezone shift.
  if (DATE_ONLY_RE.test(dateStr)) {
    return formatDate(dateStr);
  }

  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;

  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const year = d.getFullYear();

  // If the time portion is midnight-zero, treat as date-only.
  if (dateStr.endsWith('T00:00:00') || dateStr.endsWith('T00:00:00+00:00')) {
    return `${month}/${day}/${year}`;
  }

  const hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const h12 = hours % 12 || 12;
  return `${month}/${day}/${year} ${h12}:${minutes} ${ampm}`;
}
